import { ResidentialTariffStruct, VALUE_STATUS_NAMING, WaterProvider } from "../misc";
import { LongText, Unit } from "../processors";
import { CategoryChartQueryFn, CategoryChartQueryParam } from "../categoryChart";
import { FigureKpiProviders, FigureSolidDashKpiProvider } from "./categoryChartData.types";

export const TariffStructureToTextValue: Record<
    ResidentialTariffStruct,
    {
        header: string;
        value: string;
        footer: string;
    }
> = {
    blank: {
        header: null,
        value: VALUE_STATUS_NAMING.NR,
        footer: null
    },
    fixed: {
        header: null,
        value: "Fixed charge",
        footer: "Includes a fixed access charge with no consumption charge"
    },
    noChargeToResidents: {
        header: null,
        value: "No charge to residents",
        footer: "Provider does not charge customers for water and sewerage services"
    },
    fixedEntitlementAndExcessConsumptionCharge: {
        header: null,
        value: "Fixed charge and excess consumption charge",
        footer: "Includes a fixed access charge for a set volume and an excess consumption charge"
    },
    consumptionCharge: {
        header: null,
        value: "Fixed charge",
        footer: "Includes a fixed access charge with no consumption charge"
    },
    twoPartTariff: {
        header: null,
        value: "Two-part tariff",
        footer: "Includes a fixed access charge and a per kl consumption charge"
    }
};
export const getTariffStructureFigureData = async (providers: WaterProvider[]): Promise<FigureKpiProviders> => {
    const figures = providers?.map(provider => {
        const { residentialTariffStruct = "blank" } = provider;
        const value = TariffStructureToTextValue?.[residentialTariffStruct] ?? TariffStructureToTextValue?.blank;

        const figure: {
            kpiCode?: string;
            schemeType?: string;
            header?: LongText;
            value: number | string;
            decimals: number;
            unit: Unit;
            footer?: LongText;
        } = {
            kpiCode: null,
            schemeType: null,
            header:
                value?.header == null || value?.header?.trim() === ""
                    ? null
                    : {
                          processed: value?.header,
                          value: value?.header
                      },
            decimals: 0,
            value: value?.value,
            unit: "TariffStructure",
            footer:
                value?.footer == null || value?.footer?.trim() === ""
                    ? null
                    : {
                          processed: value?.footer,
                          value: value?.footer
                      }
        };

        return [figure];
    });

    return {
        waterProviders: providers,
        figures,
        footer: null,
        title: null
    };
};

export const fetchFigureTariffStructureProviderChartData: CategoryChartQueryFn = async ({
    categoryChart,
    providers
}: CategoryChartQueryParam): Promise<FigureSolidDashKpiProvider> => {
    const provider = providers?.[0];
    if (!provider || !categoryChart) return null;

    const figureData = await getTariffStructureFigureData([provider]);
    const figure = figureData?.figures?.[0]?.[0];

    if (!figure) return null;

    return {
        title: null,
        footer: null,
        graph: null,
        kpiCode: null,
        waterProvider: provider,
        schemeType: null,
        year: null,
        figure,
        unit: figure?.unit
    };
};
